"use client"

import * as React from "react"
import { NotificationItem } from "@/types/notification"
import { NotificationItemCard } from "./NotificationItemCard"
import { AppIcon } from "@/components/desk/AppIcon"
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react"

interface AppNotificationGroupProps {
  appName: string
  items: NotificationItem[]
  onDelete: (id: number) => void
  onDeleteApp?: (appName: string) => void
}

export function AppNotificationGroup({ appName, items, onDelete, onDeleteApp }: AppNotificationGroupProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)

  // Se houver apenas 1 notificação, exibe o cartão diretamente sem colapso
  if (items.length === 1) {
    return <NotificationItemCard item={items[0]} onDelete={onDelete} />
  }

  // Resolução da Logo do App no cabeçalho via AppIcon oficial
  const rawLogo = items.find(i => i.avatarUrl || i.appIcon)?.avatarUrl || items.find(i => i.avatarUrl || i.appIcon)?.appIcon
  const safeLogo = rawLogo && !rawLogo.includes("cdn.boldworkspace.com.br") ? rawLogo : undefined

  return (
    <div className="relative transition-all duration-300 ease-in-out">
      {/* CABEÇALHO DO GRUPO DO APLICATIVO */}
      <div className="flex items-center justify-between px-1 mb-1.5">
        <div className="flex items-center gap-1.5">
          <AppIcon iconUrl={safeLogo} name={appName} size="small" />
          <span className="text-[11px] font-bold text-zinc-900 dark:text-zinc-100 uppercase tracking-wider font-mono">
            {appName}
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-blue-100 dark:bg-blue-950/60 text-[#004EB1] dark:text-blue-400">
            {items.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {onDeleteApp && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDeleteApp(appName)
              }}
              className="text-[10px] font-medium text-zinc-400 hover:text-red-500 flex items-center gap-1 cursor-pointer transition-colors"
              title={`Limpar todas as notificações do ${appName}`}
            >
              <Trash2 className="size-3" />
              <span>Limpar</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-[#004EB1] dark:hover:text-blue-400 flex items-center gap-1 cursor-pointer transition-colors py-0.5 px-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <span>{isExpanded ? "Recolher" : `Expandir`}</span>
            {isExpanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        </div>
      </div>

      {isExpanded ? (
        // MODO EXPANDIDO: Mostra TODAS as notificações de forma limpa em lista
        <div className="flex flex-col gap-2 animate-in fade-in-50 duration-200">
          {items.map((item) => (
            <NotificationItemCard key={item.id} item={item} onDelete={onDelete} />
          ))}
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="w-full mt-0.5 py-1.5 px-3 rounded-lg text-[11px] font-medium text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
          >
            <span>Recolher notificações</span>
            <ChevronUp className="size-3.5" />
          </button>
        </div>
      ) : (
        // MODO COLAPSADO: Mostra apenas a notificação mais recente com botão de expandir
        <div className="flex flex-col">
          <NotificationItemCard item={items[0]} onDelete={onDelete} />
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="w-full mt-1.5 py-1.5 px-3 rounded-lg border border-dashed border-zinc-200 dark:border-zinc-800 hover:border-[#004EB1]/40 dark:hover:border-blue-500/40 bg-zinc-50/70 hover:bg-[#004EB1]/5 dark:bg-zinc-900/40 dark:hover:bg-blue-950/30 text-[11px] font-medium text-zinc-500 hover:text-[#004EB1] dark:text-zinc-400 dark:hover:text-blue-400 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
          >
            <span>+{items.length - 1} {items.length - 1 === 1 ? 'notificação' : 'notificações'}</span>
            <ChevronDown className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
